package com.evoapps.evoshift.ui.checklists

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Checklist
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Checklists
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.viewmodels.MyChecklistsViewModel
import com.evoapps.evoshift.viewmodels.TodayChecklistsViewModel
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.at_work_checklists
import evoshift.composeapp.generated.resources.my_checklists_title
import org.jetbrains.compose.resources.stringResource

/** Test tag of the dashboards' "Checklists today" card. */
const val MY_CHECKLISTS_CARD = "my_checklists_card"

/**
 * SH-121: "Checklists today" on the dashboards: the person's checklists for today's shifts at
 * this workplace with where each stands; a tap opens the shift's checklists. Nothing without any.
 */
@Composable
fun MyChecklistsCard(viewModel: MyChecklistsViewModel, onOpen: (shiftId: String) -> Unit, modifier: Modifier = Modifier) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    // SH-247: read after the dashboard: the card unfolds instead of popping in (and folds away).
    com.evoapps.evoshift.ui.theme.MotionVisibility(state.shifts.isNotEmpty()) {
    RegularCard(modifier.fillMaxWidth().testTag(MY_CHECKLISTS_CARD)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.Checklist, contentDescription = null, tint = DarkGold, modifier = Modifier.size(20.dp))
                Spacer(Modifier.width(8.dp))
                Text(
                    stringResource(Res.string.my_checklists_title),
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    color = DeepNavy.copy(alpha = 0.7f)
                )
            }
            state.shifts.forEach { row ->
                row.checklists.forEach { checklist ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .handCursor()
                            .clickable { onOpen(row.shift.id) }
                            .testTag(myChecklistTag(checklist.id))
                            .padding(vertical = 8.dp, horizontal = 4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(Modifier.weight(1f)) {
                            ChecklistHeader(checklist)
                            if (state.shifts.size > 1) {
                                Text(
                                    "${Checklists.hourOf(row.shift.plannedTime.start)}–${Checklists.hourOf(row.shift.plannedTime.end)}",
                                    style = MaterialTheme.typography.labelMedium,
                                    color = DeepNavy.copy(alpha = 0.6f)
                                )
                            }
                        }
                        Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null, tint = DarkGold)
                    }
                }
            }
        }
    }
    }
}

/**
 * SH-121: under a person in "Today at work": how many of their checklists today are done (green
 * once all are). A tap opens the day's checklists. Nothing when the shift has none.
 */
@Composable
fun AtWorkChecklists(viewModel: TodayChecklistsViewModel, shiftId: String, onOpen: () -> Unit) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val progress = state.byShift[shiftId] ?: return
    Text(
        stringResource(Res.string.at_work_checklists, progress.done, progress.total),
        style = MaterialTheme.typography.labelMedium,
        fontWeight = FontWeight.Bold,
        color = if (progress.isComplete) ChecklistDoneColor else DeepNavy.copy(alpha = 0.7f),
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .handCursor()
            .clickable(onClick = onOpen)
            .testTag(atWorkChecklistsTag(shiftId))
            .padding(vertical = 2.dp)
    )
}
